// Local task execution — run a scheduled task ON-DEVICE (Ollama / LM Studio) and
// report it via POST /schedule/:id/local-run.
//
// Extracted from useLocalScheduler so the ON-DEMAND Play button in the Chat
// sidebar routes local-model agents through the SAME local path the 9am
// scheduler uses. The cloud run-now (POST /schedule/:id/run → runAgentLoop on the
// Worker) can only run CLOUD models; a local-model agent fails there with
// "agent_loop: unknown model <X>", the run is marked failed, and nothing is
// delivered to the agent's chat. So local models must never take the cloud path.

import { apiFetch } from './api.js';
import { useAuthStore } from '../store/useAuthStore.js';
import { createLocalRunJournal, flushLocalRunJournal, runJournaledLocalTask } from './localRunJournal.js';
import { withLocalTaskCancellation } from './localTaskCancellation.js';
import { notifyLocalRunFromClient } from './ntfyClient.js';
import { extractUrls, prefetchUrls } from '../hooks/useUrlPrefetch.js';
import { runEvaluatorViaLmStudio, stashEvaluatorProposals, EVALUATOR_LOCAL_MODEL } from './evaluatorLocal.js';

function isOllamaAdapter(adapter) {
  if (!adapter) return false;
  const slug = ((adapter.name || '') + ' ' + (adapter.display_name || '')).toLowerCase();
  if (slug.includes('ollama')) return true;
  const url = (adapter.base_url || '').toLowerCase();
  return url.includes('11434');
}

export function isLmStudioAdapter(adapter) {
  if (!adapter) return false;
  const slug = ((adapter.name || '') + ' ' + (adapter.display_name || '')).toLowerCase();
  if (slug.includes('lmstudio') || slug.includes('lm studio')) return true;
  const url = (adapter.base_url || '').toLowerCase();
  return url.includes('1234');
}

function isLocalAdapter(adapter) {
  return isOllamaAdapter(adapter) || isLmStudioAdapter(adapter);
}

/** Opções de geração pra run BACKGROUND (agente agendado/Play): capa a geração
 *  (senão um resumo com reasoning gera até o contexto acabar) e estende o
 *  deadline do IPC. Sem isso TODO run com fontes prefetchadas estourava o abort
 *  de 120s do lmstudio.cjs — medido 2026-07-10: 122s timeout com gemma-4-e4b E
 *  qwen2.5-7b (D1 task 98734c34). Ollama entende num_predict; LM Studio
 *  (OpenAI-compat) max_tokens + timeoutMs. PURO. */
function backgroundRunOptions(isLm) {
  return isLm ? { max_tokens: 1024, timeoutMs: 240_000 } : { num_predict: 1024 };
}

function safeParse(jsonStr) {
  try { return JSON.parse(jsonStr || '{}'); } catch { return {}; }
}

/** A task runs locally when it's an evaluator batch, is flagged
 *  execution_target='local', or its adapter is Ollama/LM Studio. */
export function isTaskLocal(task, adaptersMap) {
  if (task?.command_type === 'evaluator_batch') return true;
  const payload = safeParse(task?.command_payload);
  if (payload.execution_target === 'local') return true;
  const adapter = (adaptersMap || {})[task?.llm_adapter_id];
  return isLocalAdapter(adapter);
}

/** The adapter to run a task with: its own, else any LM Studio adapter
 *  (mirrors the scheduler's fallback). */
export function pickAdapter(task, adaptersMap) {
  const map = adaptersMap || {};
  return map[task?.llm_adapter_id] || Object.values(map).find(isLmStudioAdapter) || null;
}

async function reportRun(taskId, body) {
  return apiFetch(`/api/code-ide/schedule/${taskId}/local-run`, { method: 'POST', body });
}

async function executeEvaluatorBatch(task, adapter) {
  const payload = safeParse(task.command_payload);
  const scope = typeof payload.scope === 'string' ? payload.scope : 'all';
  const baseUrl = adapter?.base_url || 'http://localhost:1234/v1';
  const model = payload.model_id || EVALUATOR_LOCAL_MODEL;
  const res = await runEvaluatorViaLmStudio(scope, { baseUrl, model });
  if (res.error) return { status: 'failed', output: '', error: res.error };
  if (res.model_error) return { status: 'failed', output: '', error: res.model_error };
  if (res.proposals?.length) {
    await stashEvaluatorProposals(res.proposals, { considered: res.considered });
    window.dispatchEvent(new CustomEvent('apolo:evaluator:proposals', {
      detail: { count: res.proposals.length, proposals: res.proposals },
    }));
  }
  const summary = res.proposals?.length
    ? `${res.proposals.length} proposta(s) de regra`
    : `Sem propostas (${res.gated_count ?? 0} stat(s) com gate, parsed=${res.parsed_count ?? 0})`;
  return { status: 'ok', output: summary, error: null };
}

// Modelos locais de "raciocínio" (deepseek-r1, qwen3, gemma) vazam o chain-of-
// thought no output — a notificação do celular mostrava "Here's a thinking
// process to construct the summary: 1. Analyze…" em vez do resumo (Pedro
// 2026-07-10, print). Tira (a) blocos <think>/<thinking>/<reasoning> e (b) o
// preâmbulo em prosa "Here's a thinking process…\n\n1. …\n2. …" ATÉ o primeiro
// bloco de resultado real. Conservador: sem gatilho reconhecido, devolve o
// texto intacto. PURO.
export function stripReasoning(text) {
  let s = typeof text === 'string' ? text : '';
  if (!s) return '';
  // (a) blocos com tag — todos, case-insensitive, não-gulosos.
  s = s.replace(/<(think|thinking|reasoning)>[\s\S]*?<\/\1>/gi, '');
  // (b) preâmbulo em prosa: começa com "here's a thinking process" / "let me
  // think" / "okay, let me" etc. → corta até a 1ª linha que parece resultado
  // (bullet, heading markdown, ou "Resumo/Summary:").
  if (/^\s*(here'?s a thinking process|let me think|okay,? let me|first,? i('?| wi)ll|my thinking)/i.test(s)) {
    const lines = s.split('\n');
    // Início do RESULTADO = bullet, heading, bold ou "Resumo/Summary:" — NÃO
    // lista numerada (essa é o próprio raciocínio, "1. Analyze… 2. Filter…").
    const start = lines.findIndex((l) => /^\s*([-*•#]|\*\*|resumo\b|summary\b)/i.test(l));
    if (start > 0) s = lines.slice(start).join('\n');
  }
  return s.trim();
}

/** System prompt do run LOCAL: PT forte + resumo do CONTEÚDO (não status das
 *  fontes) + sem raciocínio. O prompt antigo ("se NÃO ACESSÍVEL, diga isso")
 *  induzia o modelo local a LISTAR disponibilidade de URL em inglês em vez de
 *  resumir a notícia (Pedro 2026-07-10, print). */
function localSystemPrompt(context) {
  const rules = 'Escreva SEMPRE em português do Brasil (traduza o que estiver em inglês). Vá direto ao resultado final em tópicos curtos (bullets). NÃO mostre raciocínio ("thinking process", passos de análise) — comece já pelo primeiro tópico.';
  if (!context) return `Você é um agente autônomo que entrega o resultado pedido.\n${rules}`;
  return [
    'Você é um jornalista que resume NOTÍCIAS a partir das fontes abaixo.',
    'Resuma o CONTEÚDO — os fatos e manchetes principais. NÃO liste URLs, NÃO',
    'comente se a fonte está "disponível/acessível", NÃO invente nada além do',
    'que está nas fontes.',
    rules,
    `\nFontes:\n\n${context}`,
  ].join(' ').replace(' \n', '\n');
}

function localChatFunction(isLm, signal) {
  const provider = isLm ? window.apolo?.lmstudio : window.apolo?.ollama;
  return provider?.chatStream ? (params) => provider.chatStream(params, undefined, signal) : provider?.chat;
}

export async function executeTaskLocally(task, adapter, { signal } = {}) {
  if (signal?.aborted) return { status: 'failed', output: '', error: 'cancelled_by_user' };
  if (task.command_type === 'evaluator_batch') return executeEvaluatorBatch(task, adapter);
  const payload = safeParse(task.command_payload);
  const prompt = (payload.prompt || '').trim();
  if (!prompt) return { status: 'failed', output: '', error: 'Sem prompt no payload' };
  const isLm = isLmStudioAdapter(adapter);
  const model = payload.model_id || (isLm ? 'gemma-4-e4b' : 'qwen2.5:7b');
  const baseUrl = adapter?.base_url || (isLm ? 'http://localhost:1234/v1' : 'http://127.0.0.1:11434');

  // Pre-fetch sources se houver
  let context = '';
  try {
    const urls = extractUrls([prompt], 5);
    if (urls.length > 0 && window.apolo?.fetch) {
      context = await prefetchUrls(urls, { maxCharsPerSource: 5000 });
    }
  } catch { /* prefetch é nice-to-have */ }

  const systemContent = localSystemPrompt(context);

  const chatFn = localChatFunction(isLm, signal);
  if (!chatFn) return { status: 'failed', output: '', error: `IPC ${isLm ? 'LM Studio' : 'Ollama'} indisponível` };

  const result = await chatFn({
    baseUrl,
    model,
    options: backgroundRunOptions(isLm),
    messages: [
      { role: 'system', content: systemContent },
      // Diretiva de idioma como ÚLTIMA coisa lida = peso máximo. Modelos
      // reasoning locais (deepseek-r1-distill) teimam em inglês quando as
      // fontes são em inglês, mesmo com o system em PT (Pedro 2026-07-10).
      { role: 'user', content: `${prompt}\n\nIMPORTANTE: escreva a resposta INTEIRA em português do Brasil. NÃO responda em inglês.` },
    ],
  });

  if (!result?.ok) return { status: 'failed', output: '', error: result?.error || `${isLm ? 'LM Studio' : 'Ollama'} falhou` };
  return { status: 'ok', output: stripReasoning(result.content).slice(0, 64000), error: null };
}

/** On-demand: execute a LOCAL task NOW and report it (persist + deliver to the
 *  agent's chat via /local-run). Returns the run result. Used by the Play button. */
function localJournal() {
  const account = useAuthStore.getState().user?.id;
  if (!account) throw new Error('Entre na sua conta para executar tarefas locais');
  return createLocalRunJournal(window.localStorage, account);
}

export async function flushPendingLocalRuns() {
  return flushLocalRunJournal(localJournal(), reportRun,
    (taskId, runId) => apiFetch(`/api/code-ide/schedule/${taskId}/runs/${runId}`));
}

export async function runLocalTaskNow(task, adaptersMap, { scheduled = false, signal } = {}) {
  const result = await runJournaledLocalTask({
    task, scheduled, journal: localJournal(), report: reportRun,
    claim: (id, body) => apiFetch(`/api/code-ide/schedule/${id}/local-claim`, { method: 'POST', body }),
    execute: ({ runId }) => withLocalTaskCancellation(task.id, runId, signal,
      (runSignal) => executeTaskLocally(task, pickAdapter(task, adaptersMap), { signal: runSignal })),
  });
  notifyLocalRunFromClient(task, result).catch(() => {});
  return result;
}
